import { CheckCircle2, Database, Lightbulb, Network, Sparkles } from 'lucide-react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../lib/utils'

const TONE: Record<string, string> = {
  sky: 'bg-sky-500/15 text-sky-400',
  indigo: 'bg-indigo-500/15 text-indigo-400',
  cyan: 'bg-cyan-500/15 text-cyan-400',
  amber: 'bg-amber-500/15 text-amber-400',
  emerald: 'bg-emerald-500/15 text-emerald-400',
}

const STEPS = [
  { key: 'dataSpace', icon: Network, tone: 'sky' },
  { key: 'sources', icon: Database, tone: 'indigo' },
  { key: 'engine', icon: Sparkles, tone: 'cyan' },
  { key: 'recommendation', icon: Lightbulb, tone: 'amber' },
  { key: 'decision', icon: CheckCircle2, tone: 'emerald' },
] as const

/**
 * Conector entre dos pasos. En movil/tablet es una linea vertical (el flujo se
 * apila); en escritorio es una linea horizontal que crece para dejar ver bien
 * el camino entre las cajas.
 */
function Connector() {
  return (
    <div className="flex items-center justify-center lg:min-w-8 lg:flex-1">
      <span className="h-5 w-px bg-gradient-to-b from-border via-primary/50 to-border lg:hidden" />
      <span className="hidden h-px w-full bg-gradient-to-r from-border via-primary/50 to-border lg:block" />
    </div>
  )
}

function Node({
  stepKey,
  tone,
  icon: Icon,
  label,
}: {
  stepKey: string
  tone: string
  icon: typeof Network
  label: string
}) {
  const isDecision = stepKey === 'decision'
  const isEngine = stepKey === 'engine'
  return (
    <div
      className={cn(
        'relative z-10 flex items-center gap-3 rounded-xl border bg-card px-3.5 py-3 text-left transition-colors',
        'lg:w-36 lg:shrink-0 lg:flex-col lg:items-center lg:justify-center lg:gap-2.5 lg:px-3 lg:py-4 lg:text-center',
        isDecision ? 'border-emerald-500/40 shadow-sm shadow-emerald-500/10' : 'border-border',
      )}
    >
      <span className={cn('relative flex size-9 shrink-0 items-center justify-center rounded-lg', TONE[tone])}>
        <Icon className="size-4" />
        {isEngine ? (
          <span className="animate-float-pulse absolute inset-0 rounded-lg ring-2 ring-cyan-400/40" />
        ) : null}
      </span>
      <p
        className={cn(
          'min-w-0 flex-1 text-sm font-semibold leading-tight lg:flex-none',
          isDecision ? 'text-emerald-400' : 'text-foreground',
        )}
      >
        {label}
      </p>
    </div>
  )
}

/**
 * Hero del Campaign Studio: el flujo del dato, paso a paso, con un unico
 * "paquete de datos" que recorre de la primera caja (Data Space) a la ultima
 * (tu decision), pasando por detras de las cajas. Traducido en fi/en/es.
 */
export function DataFlowHero() {
  const { t } = useTranslation()
  return (
    <section className="relative overflow-hidden rounded-2xl border bg-gradient-to-r from-card via-card to-primary/5 px-4 py-4 shadow-sm sm:px-6 sm:py-5">
      <div className="pointer-events-none absolute -top-16 right-0 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
      <div className="relative space-y-3">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-[11px] font-bold uppercase tracking-wider text-primary">
            {t('studio.flowTitle', 'Cómo se convierte el dato en decisión')}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('studio.flowSub', 'El dato viaja del Data Space a tu decisión, con NADIA en medio.')}
          </span>
        </div>

        <div className="relative flex flex-col gap-0 lg:flex-row lg:items-stretch">
          {STEPS.map((s, i) => (
            <Fragment key={s.key}>
              <Node
                stepKey={s.key}
                tone={s.tone}
                icon={s.icon}
                label={t(`studio.flow.${s.key}`)}
              />
              {i < STEPS.length - 1 ? <Connector /> : null}
            </Fragment>
          ))}

          {/* Un unico dato que recorre el flujo de principio a fin, por detras
              de las cajas: sale de una y entra en la siguiente. */}
          <span
            aria-hidden
            className="animate-travel-v pointer-events-none absolute left-1/2 top-0 z-0 size-2.5 -translate-x-1/2 rounded-full bg-primary shadow-[0_0_12px_3px_var(--primary)] lg:hidden"
          />
          <span
            aria-hidden
            className="animate-travel-h pointer-events-none absolute left-0 top-1/2 z-0 hidden size-2.5 -translate-y-1/2 rounded-full bg-primary shadow-[0_0_12px_3px_var(--primary)] lg:block"
          />
        </div>
      </div>
    </section>
  )
}